import api from '@/services/api';

export type CalendarInvitationDto = {
  id: string;
  inviteeUserId: string | null;
  inviteeEmail: string | null;
  rsvp: string;
  invitedAt: string;
  displayName: string | null;
};

export type CalendarEventDto = {
  id: string;
  tenantId: string;
  createdByUserId?: string;
  title: string;
  description?: string | null;
  location?: string | null;
  onlineMeetingUrl?: string | null;
  timezone: string;
  startAt: string;
  endAt: string;
  allDay: boolean;
  sensitivity: string;
  status?: string;
  visibilityReason: 'invited' | 'discovery';
  isInvited: boolean;
  reminderOffsetMinutes?: number[];
  recurrenceRule?: string | null;
  recurrenceEndAt?: string | null;
  seriesEventId?: string;
  occurrenceStartAt?: string;
  /** Effective RSVP for the viewer when invited (includes per-occurrence override for recurring). */
  myRsvp?: string | null;
  /** Series-level invitation RSVP when viewing a recurring instance (may differ from myRsvp). */
  mySeriesRsvp?: string | null;
  icsUid?: string;
  icsSequence?: number;
  inviteeCount?: number;
  invitations?: CalendarInvitationDto[];
};

/** Must match backend ALLOWED_REMINDER_MINUTES. Labels: `t(\`calendar.reminder.m${minutes}\`)` in UI. */
export const CALENDAR_REMINDER_MINUTES = [5, 15, 30, 60, 120, 360, 1440, 10080] as const;

export const calendarApi = {
  list(fromIso: string, toIso: string, includeCancelled = false) {
    return api.get<{ success: boolean; data: CalendarEventDto[] }>('/calendar/events', {
      params: { from: fromIso, to: toIso, includeCancelled }
    });
  },
  get(id: string) {
    return api.get<{ success: boolean; data: CalendarEventDto }>(`/calendar/events/${id}`);
  },
  create(body: Record<string, unknown>) {
    return api.post<{ success: boolean; data: CalendarEventDto }>('/calendar/events', body);
  },
  update(id: string, body: Record<string, unknown>) {
    return api.put<{ success: boolean; data: CalendarEventDto }>(`/calendar/events/${id}`, body);
  },
  cancel(id: string, opts?: { cancelScope?: 'this_occurrence' | 'series' }) {
    return api.post<{ success: boolean }>(`/calendar/events/${id}/cancel`, {
      ...(opts?.cancelScope ? { cancelScope: opts.cancelScope } : {})
    });
  },
  remove(id: string) {
    return api.delete<{ success: boolean }>(`/calendar/events/${id}`);
  },
  setMyRsvp(
    eventId: string,
    rsvp: 'none' | 'accepted' | 'declined' | 'tentative',
    opts?: { rsvpScope?: 'series' | 'this_occurrence' }
  ) {
    return api.patch<{ success: boolean; data: CalendarEventDto }>(`/calendar/events/${eventId}/invitations/me`, {
      rsvp,
      ...(opts?.rsvpScope ? { rsvpScope: opts.rsvpScope } : {})
    });
  },
  addInvitation(eventId: string, inviteeUserId: string) {
    return api.post<{ success: boolean; data: CalendarEventDto }>(`/calendar/events/${eventId}/invitations`, {
      inviteeUserId
    });
  },
  removeInvitation(eventId: string, invitationId: string) {
    return api.delete<{ success: boolean; data: CalendarEventDto }>(
      `/calendar/events/${eventId}/invitations/${invitationId}`
    );
  },
  downloadIcs(eventId: string) {
    return api.get<Blob>(`/calendar/events/${encodeURIComponent(eventId)}/ics`, {
      responseType: 'blob'
    });
  }
};

/** User-visible label for an RSVP code (invitee list + agenda). Pass `t` from useLanguage(). */
export function formatCalendarRsvpLabel(
  rsvp: string | null | undefined,
  t: (key: string) => string
): string {
  const v = rsvp ?? 'none';
  if (v === 'accepted') return t('calendar.rsvp.label.accepted');
  if (v === 'declined') return t('calendar.rsvp.label.declined');
  if (v === 'tentative') return t('calendar.rsvp.label.tentative');
  return t('calendar.rsvp.label.none');
}
